import React, { useState, useEffect } from 'react';
import { 
  Box, Typography, Paper, Button, Divider, IconButton
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { PageLoader } from '../components/loader';
import SearchIcon from '@mui/icons-material/Search';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import OpportunityCard from '../components/services/serviceCard';
import { useAuth } from "../context/authContext";
import { getAllServices, createService } from '../services/serviceService';

import ServiceDialog from '../components/services/myservice/ServiceDialog';
import { Banner } from '../components/common/Banner';
import SectionTips from '../components/common/SectionTips';
import OpportunityFilters from '../components/services/OpportunityFilters';
import OpportunityTypeDialog from '../components/services/OpportunityTypeDialog';
const OpportunityList = () => {
  const [opportunities, setOpportunities] = useState([]);
  const [filteredOpportunities, setFilteredOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    category: 'all',
    location: 'all',
    type: 'all',
    publishType: 'all'
  });
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [serviceForm, setServiceForm] = useState({
    title: '',
    description: '',
    type_service: '',
    type_publish: '',
    category: '',
    date_publish: new Date().toISOString().split('T')[0],
    contact: '',
    email: '',
    location: '',
    name_contact: '',
    status: 'active'
  });
  const [preview, setPreview] = useState(null);
  const [loadingDialog, setLoadingDialog] = useState(false);
  
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchServices = async () => {
      try {
        setLoading(true);
        const services = await getAllServices(user);
        const activeServices = services.filter(service => service.status === 'active');
        setOpportunities(activeServices);
        setFilteredOpportunities(activeServices);
      } catch (err) {
        console.error("Error al cargar publicaciones:", err);
        setOpportunities([]);
        setFilteredOpportunities([]);
      } finally {
        setLoading(false);
      }
    };

    fetchServices();
  }, [user]);

  useEffect(() => {
    let result = [...opportunities];
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(opp => 
        opp.title?.toLowerCase().includes(term) || 
        opp.description?.toLowerCase().includes(term) ||
        opp.type_service?.toLowerCase().includes(term)
      );
    }
    
    if (filters.category !== 'all') {
      result = result.filter(opp => opp.category === filters.category);
    }
    
    if (filters.location !== 'all') {
      result = result.filter(opp => opp.location === filters.location);
    }
    
    if (filters.type !== 'all') {
      result = result.filter(opp => opp.type_service?.toLowerCase() === filters.type);
    }
    
    if (filters.publishType !== 'all') {
      result = result.filter(opp => opp.type_publish === filters.publishType);
    }
    
    setFilteredOpportunities(result);
  }, [opportunities, searchTerm, filters]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setServiceForm(prev => ({ ...prev, [name]: value }));
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSaveService = async (finalForm) => {
    setLoadingDialog(true);
    try {
      const serviceData = {
        ...finalForm,
        date_publish: finalForm.date_publish,
      };
  
      const photoFile = preview ? await fetch(preview).then(res => res.blob()) : null;
      
      await createService(serviceData, photoFile, user);
  
      setServiceForm({
        title: '',
        description: '',
        type_service: '',
        type_publish: '',
        category: '',
        date_publish: new Date().toISOString().split('T')[0],
        contact: '',
        email: '',
        location: '',
        name_contact: '',
        status: 'active'
      });
      setPreview(null);
      setOpenDialog(false);
      
      const services = await getAllServices(user);
      const activeServices = services.filter(service => service.status === 'active');
      setOpportunities(activeServices);
      setFilteredOpportunities(activeServices);
    } catch (error) {
      console.error("Error al guardar el servicio:", error);
    } finally {
      setLoadingDialog(false);
    }
  };

  const handleTypeSelection = (type, subType) => {
    setServiceForm(prev => ({
      ...prev,
      type_publish: type,
      type_service: subType || ''
    }));
    setOpenDialog(true);
  };

  const handleTypeChange = () => {
    setOpenDialog(false);
    setTimeout(() => {
      OpportunityTypeDialog.showTypeSelectionDialog(handleTypeSelection);
    }, 300);
  };

  if (loading) return <PageLoader message="Cargando oportunidades..." />;

 return (
    <Box sx={{ minHeight: '100vh', background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e7eb 100%)', py: 3 }}>
      <Box sx={{ 
        maxWidth: 'lg',
        mx: 'auto', 
        px: { xs: 2, sm: 3, md: 4 },
        display: 'flex',
        flexDirection: 'column'
      }}>
        <Banner title="Oportunidades Comerciales" message="Transforma cada desafío en una nueva oportunidad de negocio"/>
        <SectionTips
          section='la seccion de oportunidades comerciales'
          buttonTitle='¿Como usar esta sección?'
          tipsTitle='Consejos para publicar oportunidades comerciales'
          tipsList={[
            "Sé claro y específico en la descripción de tu servicio.",
            "Puedes ofertar o demandar servicios.",
            "Incluye información relevante como ubicación, tipo de servicio y contacto.",
          ]}
          finalNote='Consejo. Recuerda que una buena publicación puede marcar la diferencia.'
        />
            
        <Box sx={{ 
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 3,
          width: '100%'
        }}>
          {/* Desktop Filters Sidebar - Mantener estructura original */}
          <Paper elevation={2} sx={{
            display: { xs: 'none', md: 'flex' },
            flexDirection: 'column',
            width: 240,
            flexShrink: 0,
            borderRadius: '10px',
            p: 2,
            background: 'white',
            alignSelf: 'flex-start',
            position: 'sticky',
            top: 20
          }}>
            <Typography variant="h6" sx={{ 
              mb: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              color: '#09274A',
              fontSize: '1rem'
            }}>
              <FilterAltIcon fontSize="small" />
              Filtros
            </Typography>
            
            <Divider sx={{ my: 1 }} />
            
            <OpportunityFilters 
              desktop
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              filters={filters}
              setFilters={setFilters}
              opportunities={opportunities}
              navigate={navigate}
              showTypeSelectionDialog={() => OpportunityTypeDialog.showTypeSelectionDialog(handleTypeSelection)}
            />
          </Paper>

          {/* Opportunities Content */}
          <Box sx={{ flex: 1, width: '100%' }}>
            {/* Mobile Filters Button */}
            <Box sx={{ 
              display: { xs: 'flex', md: 'none' }, 
              justifyContent: 'space-between', 
              mb: 1,
              gap: 1
            }}>
              <Button 
                variant="outlined"
                size="small"
                startIcon={<FilterAltIcon fontSize="small" />}
                onClick={() => setMobileFiltersOpen(true)}
                sx={{
                  borderColor: '#0488AF',
                  color: '#0488AF',
                  fontSize: '0.75rem',
                  '&:hover': { 
                    borderColor: '#03779C',
                    backgroundColor: 'rgba(4, 136, 175, 0.04)'
                  },
                  flex: 1
                }}
              >
                Filtros
              </Button>
              
              <Button 
                variant="contained"
                size="small"
                sx={{ 
                  background: '#09274A',
                  color: 'white',
                  fontSize: '0.75rem',
                  '&:hover': { 
                    background: '#071D3A'
                  },
                  flex: 1
                }}
                onClick={() => navigate('/my-services')}
              >
                Mis Oport.
              </Button>

              <Button 
                variant="contained"
                size="small"
                startIcon={<AddIcon fontSize="small" />}
                sx={{ 
                  background: '#C42251',
                  color: 'white',
                  fontSize: '0.75rem',
                  '&:hover': { 
                    background: '#A01B43'
                  },
                  flex: 1
                }}
                onClick={() => OpportunityTypeDialog.showTypeSelectionDialog(handleTypeSelection)}
              >
                Nueva
              </Button>
            </Box>

            {/* Mobile Filters Drawer */}
            {mobileFiltersOpen && (
              <Paper elevation={4} sx={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 1300,
                p: 2,
                overflowY: 'auto',
                background: 'white'
              }}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                  <IconButton size="small" onClick={() => setMobileFiltersOpen(false)}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
                
                <Typography variant="h6" sx={{ mb: 2, fontSize: '1rem' }}>
                  Filtros
                </Typography>
                
                <OpportunityFilters 
                  mobile
                  searchTerm={searchTerm}
                  setSearchTerm={setSearchTerm}
                  filters={filters}
                  setFilters={setFilters}
                  opportunities={opportunities}
                  setMobileFiltersOpen={setMobileFiltersOpen}
                />
              </Paper>
            )}

            {/* Opportunities List */}
            <Box sx={{ 
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              width: '100%'
            }}>
              {filteredOpportunities.length === 0 ? (
                <Paper elevation={2} sx={{ 
                  borderRadius: '10px',
                  p: 3,
                  textAlign: 'center',
                  background: 'white'
                }}>
                  <Typography variant="h6" sx={{ mb: 1, fontSize: '1rem' }}>
                    No hay publicaciones disponibles
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'text.secondary', mb: 2, fontSize: '0.875rem' }}>
                    No se encontraron oportunidades con los filtros actuales
                  </Typography>
                </Paper>
              ) : (
                filteredOpportunities
                  .sort((a, b) => new Date(b.date_publish) - new Date(a.date_publish))
                  .map(opportunity => (
                    <Box 
                      key={opportunity.id}
                      sx={{
                        width: '100%',
                        mx: 'auto'
                      }}
                    >
                      <OpportunityCard opportunity={opportunity} />
                    </Box>
                  ))
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      <ServiceDialog
        open={openDialog}
        currentService={null}
        serviceForm={serviceForm}
        loading={loadingDialog}
        onClose={() => setOpenDialog(false)}
        onSave={handleSaveService}
        onInputChange={handleInputChange}
        onTypeChange={handleTypeChange}
        typeData={{
          type: serviceForm.type_publish,
          subType: serviceForm.type_service
        }}
      />
    </Box>
  );
};

export default OpportunityList;